import React from "react";
import { Box, Stack, InputBase, Select, MenuItem, Avatar } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { tokens, displayFont, radius } from "../theme";

/**
 * Shared, People-module-style listing primitives so attendance (and other)
 * list screens share one consistent design language: tokens theme, clean
 * tables, search/filter toolbars, status chips and stat cards.
 */

export const headCellSx = {
  // The book's table header: 12px, 600, uppercase, 0.06em, on a page-blush
  // band. The band was missing entirely — without a background the header sits
  // on the card ground and the table reads as a list of rows with a bold first
  // line rather than as a table with a head.
  fontSize: "0.75rem",
  fontWeight: 600,
  color: tokens.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  background: tokens.surface,
  borderBottom: `1px solid ${tokens.border}`,
  py: 1.5,
};

export const bodyCellSx = {
  fontSize: "0.875rem",
  color: tokens.text,
  borderBottom: `1px solid ${tokens.border}`,
  py: 1.5,
};

/**
 * A filter select.
 *
 * It was pure white on a 10px radius, which is two departures at once: white is
 * not one of the book's surfaces — the card ground is #FFFCF8 and a field at
 * rest sits on the page blush — and fields are 8px, not 10. Both overrode what
 * the theme already draws correctly, so every listing screen's filters looked
 * subtly unlike every field elsewhere in the product.
 */
export const selectSx = {
  fontSize: "0.875rem",
  borderRadius: "8px",
  background: tokens.surface,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.borderStrong },
  height: "2.75rem",
};

export const StatCard = ({ label, value, accent }) => (
  <Box
    sx={{
      flex: 1,
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      // A counter is a card in the counter band, not a panel: 14, not 12.
      borderRadius: `${radius.card}px`,
      p: "1rem 1.25rem",
    }}
  >
    <Box sx={{ fontFamily: displayFont, fontSize: "1.75rem", fontWeight: 700, color: accent || tokens.text }}>{value}</Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{label}</Box>
  </Box>
);

// A small pill. Pass an explicit color/bg, or a `tone` shortcut.
// Tints and their text colours are the brand book's picked pairs, not alpha
// derivations — an alpha wash over the blush page goes muddy.
const TONES = {
  success: { color: tokens.successText, bg: tokens.successTint },
  warning: { color: tokens.warningText, bg: tokens.warningTint },
  danger: { color: tokens.danger, bg: tokens.dangerTint },
  info: { color: tokens.infoText, bg: tokens.infoTint },
  muted: { color: tokens.pendingText, bg: tokens.pendingTint },
  // No marigold tone, deliberately. The module-screens book: "Status uses the
  // fixed five, never the brand accent, and a status badge means the same thing
  // in every module." Marigold is reserved for the one irreversible action on a
  // screen, so a status that could reach it would outrank that action. An
  // unknown tone falls back to `muted` rather than resolving to a colour.

};

export const StatusChip = ({ label, tone = "muted", color, bg }) => {
  const t = TONES[tone] || TONES.muted;
  return (
    <Box
      sx={{
        display: "inline-block",
        fontSize: "0.8125rem",
        fontWeight: 600,
        color: color || t.color,
        background: bg || t.bg,
        borderRadius: "999px",
        // 5px/12px is the book's badge padding — a component-internal value,
        // not a step on the layout spacing scale.
        px: "12px",
        py: "5px",
        textTransform: "capitalize",
      }}
    >
      {label}
    </Box>
  );
};

export const SearchInput = ({ value, onChange, placeholder = "Search", minWidth = "16rem" }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{
      border: `1px solid ${tokens.borderStrong}`,
      borderRadius: "8px",
      px: 1.5,
      height: "2.75rem",
      background: tokens.surface,
      minWidth,
    }}
  >
    <SearchIcon sx={{ fontSize: "1.125rem", color: tokens.textMuted }} />
    <InputBase
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      sx={{ fontSize: "0.875rem", flex: 1 }}
    />
  </Stack>
);

// options: [{ value, label }]. displayEmpty so an empty-string value ("All …")
// still renders its label instead of showing a blank box.
// `rest` is forwarded so a caller can name the control — a filter whose only
// label is the option it currently shows announces as that option and nothing
// else, which tells a screen-reader user what the value is but never what it
// filters.
export const FilterSelect = ({ value, onChange, options = [], ...rest }) => (
  <Select value={value} onChange={onChange} size="small" displayEmpty sx={selectSx} {...rest}>
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </Select>
);

/**
 * A person in a table cell: avatar, name, and an optional second line.
 *
 * The same fifteen lines were being retyped in People, Timesheets, Expenses and
 * Leave — and drifting each time, which is how one of them ended up with a
 * marigold avatar and another with a plum one. The initials are derived here so
 * a caller cannot pass a name and forget them.
 */
export const PersonCell = ({ name, secondary, src, size = "2rem" }) => {
  const initials = (name || "")
    .split(" ").filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join("").toUpperCase() || "?";
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Avatar
        src={src || undefined}
        sx={{
          width: size, height: size, flexShrink: 0,
          bgcolor: tokens.primary, color: tokens.onPrimary,
          fontSize: "0.6875rem", fontWeight: 600,
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontWeight: 600, color: tokens.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {name || "—"}
        </Box>
        {secondary && (
          <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {secondary}
          </Box>
        )}
      </Box>
    </Stack>
  );
};

/**
 * A person's display name from an embedded employee, or an em dash.
 *
 * "Created by" appears on half the listings in the product and each one was
 * spelling this out, including the fallback — which is how some read "—" and
 * others read "undefined undefined" when the populate came back empty.
 */
export const personName = (employee) => {
  const name = `${employee?.fname || ""} ${employee?.lname || ""}`.trim();
  return name || "—";
};
